import {inject, Injectable} from "@angular/core";
import {Location} from "@angular/common";
import {ActivatedRoute, NavigationExtras, Params, Router} from "@angular/router";
import {isDefined, isTrue, isUuid, nonBlank, safeSplit} from "../libs/utils/epic.utils";
import {Paths} from "../paths";
import {AuthService} from "./auth/auth.service";
import {LoggingService} from "./logger/logging.service";
import {MediaViewerService} from "./media/media-viewer.service";

@Injectable({providedIn: 'root'})
export class UrlService {

  private readonly router: Router = inject(Router);
  private readonly location: Location = inject(Location);
  private readonly auth: AuthService = inject(AuthService);
  private readonly log: LoggingService = inject(LoggingService);
  private readonly mediaViewerService: MediaViewerService = inject(MediaViewerService);

  private readonly newId: string = 'new';
  private readonly backLink: string = 'backlink';

  public isRouteView(): boolean {
    return this.router.url.includes('/view');
  }

  public getIdFromRoute(route: ActivatedRoute, errorMsgCode: string): string {
    const id: string | undefined = this.getValueFromRoute(route, 'id');
    if (isDefined(id) && (isUuid(id) || id === this.newId)) {
      return id;
    }
    this.routeMain(new Error(errorMsgCode));
    return '';
  }

  // For numeric ids of other systems, e.g. the support tickets in Zammad
  public getNumericIdFromRoute(route: ActivatedRoute, errorMsgCode: string): string {
    const id: string | undefined = this.getValueFromRoute(route, 'id');
    if (isDefined(id) && /^[1-9]\d{0,15}$/.test(id)) {
      return id;
    }
    this.routeMain(new Error(errorMsgCode));
    return '';
  }

  public getBackLinkFromRoute(route: ActivatedRoute): string | undefined {
    return this.getValueFromRoute(route, this.backLink);
  }

  public getValueFromRoute(route: ActivatedRoute, field: string): string | undefined {
    const value: string | null = route.snapshot.paramMap.get(field);
    return isDefined(value) && nonBlank(value) ? value : undefined;
  }

  public navigate(commands: any[], extras?: NavigationExtras): Promise<boolean> {
    this.mediaViewerService.close();
    return this.router.navigate(commands, extras);
  }

  public async navigateWithReload(commands: any[], extras?: NavigationExtras): Promise<boolean> {
    await this.router.navigateByUrl('/', {skipLocationChange: true});
    return this.navigate(commands, extras);
  }

  public navigateWithBackLink(path: string, params: Record<string, any>, backLink: string): void {
    params[this.backLink] = this.encode(backLink);
    this.navigate([path, params]).then();
  }

  public navigateToBackLink(backLink: string | undefined, search?: string, replace?: string): Promise<boolean> {
    if (isDefined(backLink)) {
      let url: string = this.decode(backLink);
      if (search && replace) {
        url = url.replace(search, replace);
      }
      const [path, ...params] = safeSplit(url, ';');
      const matrixParams: Record<string, string> = params.reduce((acc, param) => {
        const [key, value] = safeSplit(param, '=');
        acc[key] = value;
        return acc;
      }, {} as Record<string, string>);
      return this.router.navigate([path, matrixParams]);
    } else {
      this.log.error('Back link is missing');
      return Promise.resolve(false);
    }
  }

  public replaceOrExtendInUrl(search: string, replace: string): string {
    const url: string = this.getCurrentUrl();
    if (url.includes(search)) {
      const regex = new RegExp(`(${search}([^;]+))`, 'g');
      url.replace(regex, replace);
    }
    return url + replace;
  }

  public updateIdInUrlHistory(id: string): void {
    this.updateFieldInUrlHistory('id', id);
  }

  public removeFieldInUrlHistory(field: string, push: boolean = false): void {
    this.updateFieldInUrlHistory(field, undefined, push);
  }

  public removeParamFromRoute(route: ActivatedRoute, field: string): void {
    const params: Params = {...route.snapshot.params};
    if (isDefined(params[field])) {
      delete params[field];
      this.router.navigate([params], {
        relativeTo: route,
        queryParams: route.snapshot.queryParams,
        replaceUrl: true
      }).then();
    }
  }

  public createOrUpdateFieldInUrlHistory(field: string, value: string | undefined, push: boolean = false): void {
    if (nonBlank(field) && nonBlank(value)) {
      const currentUrl: string = this.getCurrentUrl();
      if (currentUrl.includes(';' + field + '=')) {
        this.updateFieldInUrlHistory(field, value, push);
      } else if (isDefined(value)) {
        this.updateUrlHistory(currentUrl + ';' + field + '=' + encodeURIComponent(value), push);
      }
    } else {
      this.removeFieldInUrlHistory(field, true);
    }
  }

  public updateFieldInUrlHistory(field: string, value: string | undefined, push: boolean = false): void {
    if (nonBlank(field)) {
      const currentUrl: string = this.getCurrentUrl();
      const regex = new RegExp(`(;${field}=([^;]+))`, 'g');
      if (isDefined(value)) {
        this.updateUrlHistory(currentUrl.replace(regex, ';' + field + '=' + value), push);
      } else if (currentUrl.includes(';' + field + '=')) {
        this.updateUrlHistory(currentUrl.replace(regex, ''), push);
      }
    }
  }

  public getQueryParam(field: string): string | undefined {
    const url = new URL(window.location.href);
    const value: string | null = url.searchParams.get(field);
    return isDefined(value) ? value : undefined;
  }

  public updateUrlHistory(url: string, push: boolean = false): void {
    if (isTrue(push)) {
      this.pushUrlHistory(url);
    } else {
      this.replaceUrlHistory(url);
    }
  }

  // Ueber Location statt window.history: Im Viewer liegt die Route im Hash (Hash-Routing), und ein Pfad wie
  // '/invoice;p=2' gehoert dorthin - nicht hinter den Pfad der index.html, wo er beim Neuladen ins Leere zeigt.
  public replaceUrlHistory(url: string): void {
    this.location.replaceState(url, '', {});
  }

  public pushUrlHistory(url: string): void {
    this.location.go(url, '', {});
  }

  /** Der Routenpfad samt Matrixparametern, ohne Abfrageteil - so, wie es zuvor `window.location.pathname` lieferte. */
  public getCurrentUrl(): string {
    return this.location.path().split('?')[0];
  }

  public redirectToUrl(url: string): void {
    window.location.href = url;
  }

  public gotoFeatures(): void {
    this.navigate([Paths.FEATURE_LIST]).then();
  }

  public routeMain(err: Error): void {
    const path: string = this.auth.isAuthenticated() ? Paths.OVERVIEW : Paths.ROOT;
    this.router.navigate([path]).then(() => {
      if (err) {
        throw err;
      }
    });
  }

  public gotoIronBackup(): void {
    this.navigate([Paths.EXPORTS]).then();
  }

  public decode(input: string): string {
    return decodeURIComponent(input);
  }

  public encode(input: string): string {
    return encodeURIComponent(input);
  }
}
